import { Component, AfterViewInit, NgZone, OnDestroy } from '@angular/core';
import { ActivatedRoute, Event, NavigationEnd, Router } from '@angular/router';
import { CookieService as NgxCookieService } from 'ngx-cookie-service';
import { UserService } from './user.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../environments/environment';
import type { EnelxNavbarElement } from 'enel-navbar/dist';

@Component({
  selector: 'app-root',
  standalone: false,
  templateUrl: './app.component.pug',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements AfterViewInit, OnDestroy {
  navUrl = '';
  currentUser: any = null;
  roles: any[] = [];
  userAllowed = false;
  isSticky = false;
  appName = '';
  productName = '';

  private intersectionObserver: IntersectionObserver | null = null;

  constructor(
    public translate: TranslateService,
    private userService: UserService,
    private ngZone: NgZone,
    private router: Router,
    private readonly route: ActivatedRoute,
    private readonly cookieService: NgxCookieService
  ) {
    this.navUrl = environment.globalNavUrl;

    this.router.events.subscribe((event: Event) => {
      if (!(event instanceof NavigationEnd)) {
        return;
      }

      let child = this.route.firstChild;
      while (child?.firstChild) {
        child = child.firstChild;
      }

      const isTraining = child?.snapshot.data['training'] === true;
      const isUtility = child?.snapshot.data['utility'] === true;
      this.appName = isTraining ? 'TRAINING_ENV' : isUtility ? 'UTILITY_PORTAL' : 'WORKFLOW_ADMIN';
      this.productName = isTraining ? 'Training Workflow Admin' : isUtility ? 'Utility Portal' : 'Workflow Admin';

      const navbarEl = document.querySelector('enelx-navbar');
      if (navbarEl) {
        navbarEl.setAttribute('app-name', this.appName);
        navbarEl.setAttribute('product-name', this.productName);
      }
    });
  }

  ngOnDestroy() {
    this.intersectionObserver?.disconnect();
  }

  ngAfterViewInit() {
    const sentinel = document.getElementById('scroll-sentinel');
    if (sentinel) {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          this.ngZone.run(() => {
            this.isSticky = !entries[0].isIntersecting;
          });
        },
        { threshold: 0 }
      );
      this.intersectionObserver.observe(sentinel);
    }

    const navbarElement = document.querySelector('enelx-navbar') as any;
    if (navbarElement) {
      navbarElement.addEventListener('user', (event: CustomEvent) => {
        this.ngZone.run(() => this.initializeUser(event.detail?.user));
      });

      navbarElement.addEventListener('userRoles', (event: CustomEvent) => {
        this.ngZone.run(() => {
          this.applyUserRoles(event.detail?.userRoles);
        });
      });

      this.waitForNavbarUser(navbarElement);
    }

    this.handleSettingTranslations();
  }

  private waitForNavbarUser(navbarElement: any, attempts = 0) {
    if (attempts > 5) {
      return;
    }

    setTimeout(() => {
      try {
        const componentInstance = navbarElement.ngElementStrategy?.componentRef?.instance as EnelxNavbarElement;
        if (componentInstance && typeof componentInstance.user === 'function') {
          const user = componentInstance.user();
          if (user) {
            this.initializeUser(user);
          }
          this.applyUserRoles(componentInstance.userRoles());
        }
      } catch (e) {
        console.error(e);
      }
      this.waitForNavbarUser(navbarElement, attempts + 1);
    }, 500);
  }

  private applyUserRoles(userRoles: any) {
    if (!userRoles) {
      return;
    }

    this.roles = userRoles;
    this.userService.setUserRoles(userRoles);
  }

  private initializeUser(user: any) {
    if (user && !this.currentUser) {
      this.userService.formatUser(user);
      this.currentUser = this.userService.user;

      const resources = this.userService.user?.resources || [];
      const found = resources.some((resource: any) => {
        const resourceName = resource.resource_name || resource.resourceName;
        return resourceName === 'WORKFLOW_ADMIN' || resourceName === 'UTILITY_PORTAL_DISPATCH';
      });

      this.userAllowed = !!found;
    }
  }

  handleSettingTranslations() {
    const brand = (this.cookieService.get('default_brand') || 'ENELX').toUpperCase();
    const locale = (this.cookieService.get('locale') || 'en-us').replace('_', '-').toLowerCase();
    const setting = `${locale}.${brand}`;
    console.log(`Setting translation to: ${setting}`);
    this.translate.use(setting);
  }
}
